// @flow

import * as React from 'react';
import {
  graphql,
  useRelayEnvironment,
  QueryRenderer,
  useFragment,
  useMutation,
} from 'react-relay';
import type { RenderProps } from 'react-relay';
import { Flex, Box } from 'react-system';
import { useFormik, Form } from '@realadvisor/form';
import { useCaptcha } from '@realadvisor/hooks';
import {
  TextField,
  FormControl,
  InputLabel,
  Button,
  Avatar,
  AppBar,
  FormHelperText,
} from '@material-ui/core';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import * as icons from '../../icons';
import { isEmail } from '../../utils/isEmail';
import { InlineMarked } from '../../controls/marked';
import { LoadingButton } from '../../controls/loading-button';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { useUserData } from '../../hooks/user-data';
import { useAnalytics } from '../../controls/analytics';
import { useSeoUserInfo } from '../../shared/seo-user-info';
import {
  PhoneInput,
  getPhonePrefix,
} from '../../controls/switzerland-phone-input';
import type { aggregatesListingCampaignEnquiryFormUpsertMutation } from './__generated__/aggregatesListingCampaignEnquiryFormUpsertMutation.graphql';
import type { aggregatesListingCampaignEnquiry_listing$key } from './__generated__/aggregatesListingCampaignEnquiry_listing.graphql';
import type { aggregatesListingCampaignEnquiryMeQueryResponse } from './__generated__/aggregatesListingCampaignEnquiryMeQuery.graphql';

type Props = {|
  listing: aggregatesListingCampaignEnquiry_listing$key,
  isMobile?: boolean,
  onClose?: () => void,
|};

const isEmpty = value =>
  value == null || (typeof value === 'string' && value.trim().length === 0);

export const EnquirySent = (): React.Node => {
  const { t } = useLocale();
  const { text, colors } = useTheme();
  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
      css={{ textAlign: 'center', height: '100%' }}
    >
      <Box pt={3}>
        <Avatar
          style={{
            backgroundColor: colors.green[500],
            width: 80,
            height: 80,
          }}
        >
          <icons.CheckMark size={70} />
        </Avatar>
      </Box>
      <Box pt={3} css={text.subtitle0}>
        {t('yourMessageSent')}
      </Box>
      <Box py={2} css={text.body1}>
        <InlineMarked>{t('yourMessageSentDesc')}</InlineMarked>
      </Box>
    </Flex>
  );
};

const BaseCampaignEnquiry = props => {
  const listing = useFragment(
    graphql`
      fragment aggregatesListingCampaignEnquiry_listing on AggregatesListing {
        id
        originalId
        encodedId
        slug
        offerType
        propertyType
        propertyMainType
        compositePropertyType
        numberOfRooms
        numberOfBathrooms
        livingSurface
        landSurface
        salePrice
        grossRentMonthly
        currency
        constructionYear
        locality
        stateObject {
          label
        }
        municipalityObject {
          label
        }
        streetNumber
        route
        postcode
        lat
        lng
        agencyName
        agencyReference
        lotReference
        url {
          originalDomain
          url
        }
      }
    `,
    props.listing,
  );

  const { me } = props;

  const [createEnquiry, enquiryCreating] =
    useMutation<aggregatesListingCampaignEnquiryFormUpsertMutation>(
      graphql`
        mutation aggregatesListingCampaignEnquiryFormUpsertMutation(
          $input: ListingCampaignClickoutInput!
        ) {
          listingCampaignClickout(input: $input) {
            status
            errors {
              captcha
              phone
              offline
            }
          }
        }
      `,
    );

  const { track } = useAnalytics();
  const [enquirySent, setEnquirySent] = React.useState(false);
  const { text, colors } = useTheme();
  const { t } = useLocale();
  const { countryCode, reCaptchaKeyV2, reCaptchaKey } = useUserData();
  const captcha = useCaptcha({ reCaptchaKeyV2, reCaptchaKeyV3: reCaptchaKey });
  const seoUserData = useSeoUserInfo();

  const initialValues = {
    firstName: me?.firstName ?? '',
    lastName: me?.lastName ?? '',
    email: me?.primaryEmail?.email ?? '',
    phoneNumber: me?.primaryPhoneNumber?.number ?? getPhonePrefix(countryCode),
    message: t('defaultCampaignEnquiryMessage'),
  };

  const [messageLength, setMessageLength] = React.useState(
    initialValues.message.length,
  );

  const formik = useFormik({
    initialValues,
    validate: values => {
      const errors = {};
      if (isEmpty(values.email)) {
        errors.email = t('emailRequired');
      } else if (!isEmail(values.email)) {
        errors.email = t('invalidEmail');
      }
      if (isEmpty(values.firstName)) {
        errors.firstName = t('firstNameRequired');
      }
      if (isEmpty(values.lastName)) {
        errors.lastName = t('lastNameRequired');
      }
      if (isEmpty(values.phoneNumber)) {
        errors.phoneNumber = t('phoneNumberRequired');
      }
      if (isEmpty(values.message)) {
        errors.message = t('messageRequired');
      } else if (values.message.length > 3000) {
        errors.message = t('messageLengthExceeded');
      }

      return errors;
    },
    onSubmit: values => {
      captcha.requestToken((recaptchaToken, recaptchaVersion) => {
        createEnquiry({
          variables: {
            input: {
              listingId: listing.id,
              enquiry: {
                client: 'agg',
                originalId: listing.originalId,
                firstName: values.firstName,
                lastName: values.lastName,
                fullName: [values.firstName, values.lastName].join(' '),
                email: values.email,
                phone: values.phoneNumber,
                message: values.message,
              },
              recaptchaToken,
              recaptchaVersion,
              landingPageUrl: seoUserData?.landing_page_url ?? null,
              currentUrl: window.location.href,
              referrer: seoUserData?.referrer ?? null,
            },
          },
          onCompleted: payload => {
            const status = payload.listingCampaignClickout?.status;
            const errors = payload.listingCampaignClickout?.errors;
            if (errors) {
              if (errors.captcha != null) {
                captcha.verifyUser();
              }
              if (errors.phone != null) {
                formik.setResponseErrors({ phoneNumber: errors.phone });
              }
              if (errors.offline != null) {
                formik.setResponseErrors({ message: errors.offline });
              }
            }

            if (status === true) {
              track('RA.Web.Listing.Enquiry.Sent', {
                category: 'Campaign',
                label: listing.url?.originalDomain,
                id: listing.id,
                encodedId: listing.encodedId,
                slug: listing.slug,
                offerType: listing.offerType,
                propertyType: listing.propertyType,
                propertyMainType: listing.propertyMainType,
                compositePropertyType: listing.compositePropertyType,
                numberOfRooms: listing.numberOfRooms,
                numberOfBathrooms: listing.numberOfBathrooms,
                livingSurface: listing.livingSurface,
                landSurface: listing.landSurface,
                salePrice: listing.salePrice,
                grossRentMonthly: listing.grossRentMonthly,
                currency: listing.currency,
                constructionYear: listing.constructionYear,
                locality: listing.locality,
                state: listing.stateObject?.label,
                municipality: listing.municipalityObject?.label,
                streetNumber: listing.streetNumber,
                route: listing.route,
                postcode: listing.postcode,
                lat: listing.lat,
                lng: listing.lng,
                agencyName: listing.agencyName,
                agencyReference: listing.agencyReference,
                lotReference: listing.lotReference,
                url: listing.url?.url,
              });
            }
            setEnquirySent(status);
          },
        });
      });
    },
  });

  const { values, setValues, errors, setTouched } = formik;

  return (
    <>
      {enquirySent === true ? (
        <Box css={{ height: '80%' }}>
          <EnquirySent />
          {props.isMobile === true && (
            <AppBar color="inherit" css={{ bottom: 0, top: 'auto' }}>
              <Box px={3} py={2}>
                <Button color="primary" onClick={props.onClose}>
                  {t('continueBrowsing')}
                </Button>
              </Box>
            </AppBar>
          )}
        </Box>
      ) : (
        <Form onSubmit={formik.submitForm}>
          {props.isMobile !== true && (
            <Box py={3} css={text.subtitle0}>
              {t('contactAdvertiser')}
            </Box>
          )}
          <Box pb={2}>
            <TextField
              required={true}
              label={t('firstName')}
              placeholder={t('enterFirstName')}
              variant="filled"
              value={values.firstName}
              error={errors.firstName != null}
              helperText={errors.firstName ?? null}
              FormHelperTextProps={{
                variant: 'standard',
              }}
              onChange={event =>
                setValues({
                  firstName: event.target.value,
                })
              }
              onBlur={() => setTouched({ firstName: true })}
            />
          </Box>
          <Box py={2}>
            <TextField
              required={true}
              label={t('lastName')}
              placeholder={t('enterLastName')}
              variant="filled"
              value={values.lastName}
              error={errors.lastName != null}
              helperText={errors.lastName ?? null}
              FormHelperTextProps={{
                variant: 'standard',
              }}
              onChange={event =>
                setValues({
                  lastName: event.target.value,
                })
              }
              onBlur={() => setTouched({ lastName: true })}
            />
          </Box>
          <Box py={2}>
            <TextField
              required={true}
              label={t('emailAddress')}
              placeholder={t('email')}
              variant="filled"
              value={values.email}
              error={errors.email != null}
              helperText={errors.email ?? null}
              FormHelperTextProps={{
                variant: 'standard',
              }}
              onChange={event =>
                setValues({
                  email: event.target.value,
                })
              }
              onBlur={() => setTouched({ email: true })}
            />
          </Box>
          <Box py={2}>
            <FormControl
              variant="filled"
              required={true}
              error={errors.phoneNumber != null}
            >
              <InputLabel>{t('phoneNumber')}</InputLabel>
              <PhoneInput
                variant="filled"
                value={values.phoneNumber}
                onChange={value => setValues({ phoneNumber: value })}
                onBlur={() => setTouched({ phoneNumber: true })}
              />
              {errors.phoneNumber != null && (
                <FormHelperText variant="standard">
                  {errors.phoneNumber}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box py={2}>
            <FormControl
              variant="filled"
              required={true}
              error={errors.message != null}
            >
              <TextareaAutosize
                style={{
                  ...text.body1,
                  width: '100%',
                  backgroundColor: colors.grey[100],
                  border: 'none',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: '8px',
                }}
                rows={5}
                placeholder={t('writeYourQuery')}
                onChange={event => {
                  setMessageLength(event.target.value.length);
                  setValues({ message: event.target.value });
                }}
                defaultValue={values.message}
              />
              {errors.message != null && (
                <FormHelperText variant="standard">
                  {errors.message}
                </FormHelperText>
              )}
            </FormControl>
            <Flex justifyContent="flex-end">
              <Box css={text.caption}>{messageLength}/3000</Box>
            </Flex>
          </Box>
          <Box
            py={2}
            px={[3, 0]}
            css={
              props.isMobile === true && {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                top: 'auto',
              }
            }
          >
            {captcha.element}
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={enquiryCreating}
              loading={enquiryCreating}
              onClick={() => {
                formik.submitForm();
                track('RA.Web.Listing.Enquiry.Click', {
                  category: 'Campaign',
                  label: listing.url?.originalDomain,
                  id: listing.id,
                  encodedId: listing.encodedId,
                  slug: listing.slug,
                  offerType: listing.offerType,
                  propertyType: listing.propertyType,
                  propertyMainType: listing.propertyMainType,
                  compositePropertyType: listing.compositePropertyType,
                  numberOfRooms: listing.numberOfRooms,
                  numberOfBathrooms: listing.numberOfBathrooms,
                  livingSurface: listing.livingSurface,
                  landSurface: listing.landSurface,
                  salePrice: listing.salePrice,
                  grossRentMonthly: listing.grossRentMonthly,
                  currency: listing.currency,
                  constructionYear: listing.constructionYear,
                  locality: listing.locality,
                  state: listing.stateObject?.label,
                  municipality: listing.municipalityObject?.label,
                  streetNumber: listing.streetNumber,
                  route: listing.route,
                  postcode: listing.postcode,
                  lat: listing.lat,
                  lng: listing.lng,
                  agencyName: listing.agencyName,
                  agencyReference: listing.agencyReference,
                  lotReference: listing.lotReference,
                  url: listing.url?.url,
                });

                window.dataLayer.push({
                  event: 'submit_enquiry_form',
                  encoded_id: listing.encodedId,
                  id: listing.id,
                  slug: listing.slug,
                  offer_type: listing.offerType,
                  property_type: listing.propertyType,
                  property_main_type: listing.propertyMainType,
                  composite_property_type: listing.compositePropertyType,
                  number_of_rooms: listing.numberOfRooms,
                  number_of_bathrooms: listing.numberOfBathrooms,
                  living_surface: listing.livingSurface,
                  land_surface: listing.landSurface,
                  sale_price: listing.salePrice,
                  gross_rent_monthly: listing.grossRentMonthly,
                  currency: listing.currency,
                  construction_year: listing.constructionYear,
                  locality: listing.locality,
                  state: listing.stateObject?.label,
                  municipality: listing.municipalityObject?.label,
                  street_number: listing.streetNumber,
                  route: listing.route,
                  postcode: listing.postcode,
                  lat: listing.lat,
                  lng: listing.lng,
                  agency_name: listing.agencyName,
                  agency_reference: listing.agencyReference,
                  lot_reference: listing.lotReference,
                  url: listing.url?.url,
                });
              }}
            >
              {t('sendEnquiry')}
            </LoadingButton>
          </Box>
        </Form>
      )}
    </>
  );
};

export const AggregatesListingCampaignEnquiry = (props: Props): React.Node => {
  const environment = useRelayEnvironment();
  return (
    <QueryRenderer
      environment={environment}
      query={graphql`
        query aggregatesListingCampaignEnquiryMeQuery {
          me {
            firstName
            lastName
            primaryEmail {
              email
            }
            primaryPhoneNumber {
              number
            }
          }
        }
      `}
      variables={{}}
      render={({
        props: data,
        error,
      }: RenderProps<aggregatesListingCampaignEnquiryMeQueryResponse>) =>
        (data != null || error != null) && (
          <BaseCampaignEnquiry
            me={data?.me}
            listing={props.listing}
            onClose={props.onClose}
          />
        )
      }
    />
  );
};
